import React, { useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

type Model = { _id: string; fileName: string };
type ModelGroup = {
  _id: string;
  name: string;
  modelIds: string[];
  toolData: any[];
};

export default function UnmappedFilesPanel({
  availableModels,
  selectedModelIds,
  onModelSelection,
  getControllerIcon,
  getControllerName,
  completedModels = [],
  completedGroups = [],
  onDeleteGroup,
  allModels = [],
}: {
  availableModels: Model[];
  selectedModelIds: string[];
  onModelSelection: (selectedValues: string[]) => void;
  getControllerIcon: (
    fileName: string
  ) => React.ComponentType<{ className?: string }> | null;
  getControllerName: (fileName: string) => string;
  completedModels?: Model[];
  completedGroups?: ModelGroup[];
  onDeleteGroup?: (groupId: string) => void;
  allModels?: Model[];
}) {
  const groupedModels = useMemo(() => {
    const grouped: { [key: string]: Model[] } = {};
    availableModels.forEach((model) => {
      const key = getControllerName(model.fileName) || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(model);
    });
    return grouped;
  }, [availableModels, getControllerName]);

  const groupedCompletedModels = useMemo(() => {
    const grouped: { [key: string]: Model[] } = {};
    completedModels.forEach((model) => {
      const key = getControllerName(model.fileName) || 'Unknown';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(model);
    });
    return grouped;
  }, [completedModels, getControllerName]);

  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  // Update expanded items whenever groups change to keep them open
  React.useEffect(() => {
    const unmappedKeys = Object.keys(groupedModels);
    const completedKeys = Object.keys(groupedCompletedModels).map(
      (key) => `completed-${key}`
    );
    setExpandedItems([...unmappedKeys, ...completedKeys]);
  }, [groupedModels, groupedCompletedModels]);

  const handleModelToggle = (modelId: string, checked: boolean) => {
    const availableModelIds = availableModels.map((m) => m._id);
    const currentSelection = selectedModelIds.filter((id) =>
      availableModelIds.includes(id)
    );
    if (checked) {
      if (!currentSelection.includes(modelId)) currentSelection.push(modelId);
    } else {
      const index = currentSelection.indexOf(modelId);
      if (index > -1) currentSelection.splice(index, 1);
    }
    onModelSelection(currentSelection);
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0 border-b py-2 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            Select a Gcode file to start adding tool data to it
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        <ScrollArea className="h-full px-4 py-3">
          {Object.keys(groupedModels).length === 0 &&
          Object.keys(groupedCompletedModels).length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm font-medium mb-2">
                No Gcode files available
              </p>
              <p className="text-xs text-muted-foreground">
                Upload Gcode files to start adding tool data.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Unmapped (Active) Files */}
              {Object.keys(groupedModels).length > 0 && (
                <Accordion
                  type="multiple"
                  className="space-y-2"
                  value={expandedItems}
                  onValueChange={setExpandedItems}
                >
                  {Object.entries(groupedModels).map(([group, models]) => (
                    <AccordionItem
                      key={group}
                      value={group}
                      className="border rounded-lg"
                    >
                      <AccordionTrigger className="px-4 py-3 hover:no-underline">
                        <div className="flex items-center gap-3">
                          {(() => {
                            const ControllerIcon = getControllerIcon(
                              models[0]?.fileName
                            );
                            return ControllerIcon ? (
                              <ControllerIcon className="w-5 h-5" />
                            ) : (
                              <svg
                                className="w-5 h-5"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                              </svg>
                            );
                          })()}
                          <span className="font-medium">{group}</span>
                          <Badge variant="secondary" className="ml-auto">
                            {models.length}
                          </Badge>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-3">
                        <div className="space-y-2">
                          {models.map((model) => (
                            <div
                              key={model._id}
                              className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 ml-4 cursor-pointer"
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                handleModelToggle(
                                  model._id,
                                  !selectedModelIds.includes(model._id)
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleModelToggle(
                                    model._id,
                                    !selectedModelIds.includes(model._id)
                                  );
                                }
                              }}
                            >
                              <Checkbox
                                checked={selectedModelIds.includes(model._id)}
                                onCheckedChange={(checked) =>
                                  handleModelToggle(
                                    model._id,
                                    checked as boolean
                                  )
                                }
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">
                                  {model.fileName}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}

              {/* Completed Groups */}
              {completedGroups.length > 0 && (
                <div className="space-y-2 mt-6">
                  <div className="px-2 py-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Configured Groups
                    </p>
                  </div>
                  <div className="space-y-2">
                    {completedGroups.map((group) => (
                      <div
                        key={group._id}
                        className="border rounded-lg bg-card hover:shadow-sm transition-shadow"
                      >
                        <div className="px-4 py-3">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-b from-green-600 to-green-400 flex items-center justify-center flex-shrink-0">
                                <svg
                                  className="w-5 h-5 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {group.name}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {group.modelIds.length} file
                                  {group.modelIds.length !== 1
                                    ? 's'
                                    : ''} • {group.toolData.length} tool
                                  {group.toolData.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            {onDeleteGroup && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteGroup(group._id);
                                }}
                                className="text-destructive hover:text-destructive/90 hover:bg-destructive/10 flex-shrink-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="px-4 pb-3 border-t pt-2">
                          <div className="space-y-1 ml-2">
                            {group.modelIds.map((modelId) => {
                              const model = allModels.find(
                                (m) => m._id === modelId
                              );
                              if (!model) return null;
                              return (
                                <div
                                  key={modelId}
                                  className="flex items-center gap-2 p-1.5 rounded text-sm text-muted-foreground"
                                >
                                  <svg
                                    className="w-3.5 h-3.5 flex-shrink-0"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  <span className="truncate">
                                    {model.fileName}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
